<template>
  <v-container class="pa-4 container-body">
    <div v-if="htmlContent" v-html="htmlContent" class="publication-html" />
    <v-divider class="my-4" />
    <div class="text-center">
      <v-btn
        @click="goToPublications"
        class="ma-2"
        color="primary"
        variant="tonal"
      >
        Back to Publications
      </v-btn>
    </div>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import slugList from "@/assets/publicationSlugs.json";

const route = useRoute();
const router = useRouter();

const rawSlug = ref(route.params.slug.toLowerCase());
const htmlContent = ref("");

// Find exact match from slug list (case-insensitive)
const matchedSlug = computed(() => {
  return slugList.find((s) => s.toLowerCase() === rawSlug.value) || null;
});

const currentIndex = computed(() =>
  slugList.findIndex((s) => s === matchedSlug.value)
);
const previousSlug = computed(() => slugList[currentIndex.value - 1] || null);
const nextSlug = computed(() => slugList[currentIndex.value + 1] || null);

const loadHtml = async () => {
  if (!matchedSlug.value) {
    htmlContent.value = "<p>Story not found.</p>";
    return;
  }

  try {
    const response = await fetch(`/publications/${matchedSlug.value}.html`, {
      method: "GET",
      headers: {
        Accept: "text/html",
      },
      mode: "cors", // not strictly needed, but makes failure clearer
    });
    htmlContent.value = await response.text();
  } catch (err) {
    htmlContent.value = "<p>Error loading story.</p>";
  }
};

watch(
  () => route.params.slug,
  (newSlug) => {
    rawSlug.value = newSlug.toLowerCase();
    loadHtml();
  }
);

onMounted(loadHtml);

const goToPrevious = () => {
  if (previousSlug.value) {
    router.push({
      name: "PublicationViewer",
      params: { slug: previousSlug.value },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const goToNext = () => {
  if (nextSlug.value) {
    router.push({
      name: "PublicationViewer",
      params: { slug: nextSlug.value },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const goToPublications = () => {
  router.push({ name: "Publications" });
};
</script>

<style>
.container-body {
  background-color: #f0e6d2;
  margin-top: 4vh;
}
.publication-html {
  max-width: 700px;
  margin: auto;
}
.body-class {
  background-color: #f0e6d2;
  color: #3e3e3e;
  font-family: "Georgia", serif;
  line-height: 1.6;
  padding: 2rem;
  max-width: 700px;
  margin: auto;
}

h1 {
  color: #c8a2c8;
  font-size: 2rem;
  margin-bottom: 0.2em;
}

h2 {
  color: #5a3e36;
  font-size: 1.2rem;
  font-weight: normal;
  margin-top: 0;
  margin-bottom: 2rem;
}

a {
  color: #a3b18a;
  text-decoration: underline;
}

a:hover {
  color: #5a3e36;
}

p {
  margin-bottom: 1.25rem;
}

em {
  font-style: italic;
}

strong {
  font-weight: bold;
}

.byline {
  font-size: 0.9rem;
  color: #5a3e36;
  margin-bottom: 2rem;
}

hr {
  border: none;
  border-top: 1px solid #cac4c3;
  margin: 2rem 0;
}
</style>
